/**
 * Cricket player headshots at `Cricket/Player Images/{slug}.png`
 * (CloudFront → cricket8-web-content; same flat pattern as Tennis/Player Images).
 */

import { DEFAULT_SPORT_IMAGES_CDN, sportImagePublicBase } from "./sportImageCdn";

const DEFAULT_CRICKET_PLAYER_IMAGE_S3_PREFIX = "Cricket/Player Images";

const LEGACY_CDN_PREFIX = "IPL+Team+Images";

export const CRICKET_PLAYER_IMAGE_S3_BASE = sportImagePublicBase(
  DEFAULT_CRICKET_PLAYER_IMAGE_S3_PREFIX,
  process.env.CRICKET_PLAYER_IMAGE_BASE
);

export function slugifyCricketPlayerName(name: string): string {
  return String(name || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\./g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

const cleanNameToken = (token: string) => String(token || "").replace(/\./g, "").trim();

const isLikelyInitial = (token: string) => {
  const t = cleanNameToken(token);
  return t.length >= 1 && t.length <= 2;
};

const splitPlayerNameParts = (name: string) =>
  String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

/**
 * Name variants aligned with backend cricketPlayerNameVariantsForImageLookup.
 * Never emit a bare surname — shared surnames collide (e.g. Potts).
 * Keep initial+surname ("G Potts" → g-potts) for feed-friendly aliases.
 */
export function cricketPlayerNameVariantsForImageLookup(playerName: string): string[] {
  const variants: string[] = [];
  const seen = new Set<string>();
  const addVariant = (value: string) => {
    const trimmed = String(value || "").trim();
    if (!trimmed || seen.has(trimmed)) return;
    seen.add(trimmed);
    variants.push(trimmed);
  };

  const addInitialSurname = (firstClean: string, lastClean: string) => {
    if (firstClean.length < 2 || lastClean.length <= 2) return;
    if (isLikelyInitial(firstClean)) return;
    addVariant(`${firstClean[0]} ${lastClean}`);
  };

  const raw = String(playerName || "").trim();
  if (!raw) return variants;

  addVariant(raw);

  const comma = raw.indexOf(",");
  if (comma > 0) {
    const last = raw.slice(0, comma).trim();
    const first = raw.slice(comma + 1).trim();
    if (first && last) {
      addVariant(`${first} ${last}`);
      addInitialSurname(cleanNameToken(first), cleanNameToken(last));
    }
  }

  if (!raw.includes(",")) {
    const parts = splitPlayerNameParts(raw);

    if (parts.length === 2) {
      const first = parts[0];
      const last = parts[1];
      const firstClean = cleanNameToken(first);
      const lastClean = cleanNameToken(last);

      if (isLikelyInitial(first) && lastClean.length > 2) {
        // "G. Potts" / "G Potts" — keep raw only (→ g-potts). No bare surname.
      } else if (firstClean.length > 2 && lastClean.length > 2) {
        addVariant(`${lastClean}, ${firstClean}`);
        addInitialSurname(firstClean, lastClean);
      }

      if (parts[1].length <= 2) {
        addVariant(`${parts[1]} ${parts[0]}`.replace(/\./g, "").trim());
      }
    }

    if (parts.length >= 3) {
      const firstClean = cleanNameToken(parts[0]);
      const lastClean = cleanNameToken(parts[parts.length - 1]);
      const middleClean = parts
        .slice(1, -1)
        .map(cleanNameToken)
        .filter(Boolean);
      // Particles/middle names ("de Villiers") must stay in the initial alias.
      // Dropping them yields "M Villiers" → m-villiers, which collides with
      // unrelated players (Matthew de Villiers vs Mady Villiers).
      const compoundSurname =
        middleClean.length > 0 ? [...middleClean, lastClean].join(" ") : lastClean;
      if (firstClean.length > 1 && lastClean.length > 2) {
        addVariant(`${firstClean} ${lastClean}`);
        addVariant(`${lastClean}, ${firstClean}`);
        addInitialSurname(firstClean, compoundSurname);
      }
    }
  }

  return variants;
}

export function cricketPlayerImageS3Url(playerName: string): string {
  const slug = slugifyCricketPlayerName(playerName);
  if (!slug) return "";
  return `${CRICKET_PLAYER_IMAGE_S3_BASE}${slug}.png`;
}

/** Legacy: `{CDN}/IPL+Team+Images/{team}/{Player_Name}.png` */
export function cricketPlayerImageLegacyCdnUrl(teamName: string, playerName: string): string {
  const safeTeam = String(teamName || "").trim();
  const safePlayer = String(playerName || "").trim();
  const cdn = (process.env.CDN_ENDPOINT || DEFAULT_SPORT_IMAGES_CDN).replace(/\/$/, "");
  if (!safeTeam || !safePlayer) return "";
  return `${cdn}/${LEGACY_CDN_PREFIX}/${safeTeam.replace(/ /g, "+")}/${safePlayer.replace(/ /g, "_")}.png`;
}

/**
 * Ordered headshot URLs: flat S3 by player name variants, then legacy team-folder CDN paths.
 */
export function getOrderedCricketPlayerImageUrls(
  playerName: string,
  teamNames: string | readonly string[] | null | undefined = [],
): string[] {
  const safePlayer = String(playerName || "").trim();
  if (!safePlayer) return [];

  const urls: string[] = [];
  const seen = new Set<string>();
  const push = (url: string) => {
    if (!url || seen.has(url)) return;
    seen.add(url);
    urls.push(url);
  };

  for (const variant of cricketPlayerNameVariantsForImageLookup(safePlayer)) {
    push(cricketPlayerImageS3Url(variant));
  }

  const teams = Array.isArray(teamNames) ? teamNames : teamNames ? [teamNames] : [];
  for (const team of teams) {
    for (const variant of cricketPlayerNameVariantsForImageLookup(safePlayer)) {
      push(cricketPlayerImageLegacyCdnUrl(String(team || ""), variant));
    }
  }
  return urls;
}
